import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import MapComponent from '../Map/MapComponent';
import { useGetAllUsersQuery, useGetAllLocationsQuery } from '../../redux/adminApiSlice';
import { User } from '../../types/interfaces';

interface Bounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface CustomMarker {
  id: string;
  position: {
    lat: number;
    lng: number;
  };
  element?: React.ReactNode;
  onClick?: () => void;
  tooltip?: React.ReactNode;
  icon?: any;
  label?: any;
}

const GodView: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [mapBounds, setMapBounds] = useState<Bounds | null>(null);

  // NYC coordinates as default center
  const defaultCenter = {
    lat: 40.7128,
    lng: -74.0060
  };

  const { data: usersData, error: usersError } = useGetAllUsersQuery(undefined, {
    pollingInterval: 30000 // Poll every 30 seconds, matching the original interval
  });

  const { data: locationsData, error: locationsError } = useGetAllLocationsQuery();

  useEffect(() => {
    if (usersData && locationsData) {
      setUsers(usersData);

      // Calculate bounds from all user locations
      const usersWithCoords = usersData.filter(
        (user) => user.location?.coordinates?.length === 2
      );

      if (usersWithCoords.length > 0) {
        const bounds = usersWithCoords.reduce((acc: Bounds, user) => {
          const [lng, lat] = user.location!.coordinates;
          return {
            north: Math.max(acc.north, lat),
            south: Math.min(acc.south, lat),
            east: Math.max(acc.east, lng),
            west: Math.min(acc.west, lng)
          };
        }, {
          north: -90,
          south: 90,
          east: -180,
          west: 180
        } as Bounds);

        // Add padding to bounds
        const padding = 0.1; // 10% padding
        const latSpan = bounds.north - bounds.south;
        const lngSpan = bounds.east - bounds.west;
        
        setMapBounds({
          north: bounds.north + latSpan * padding,
          south: bounds.south - latSpan * padding,
          east: bounds.east + lngSpan * padding,
          west: bounds.west - lngSpan * padding
        });
      }
    }

    if (usersError || locationsError) {
      console.error('Error fetching data:', usersError || locationsError);
      setError((usersError || locationsError) as string || 'Failed to fetch data');
    }
  }, [usersData, locationsData, usersError, locationsError]);

  const usersWithCoords = users.filter(user => user.location?.coordinates);

  const userMarkers = usersWithCoords.map(user => {
    if (!user.location?.coordinates) return null;
    const [lng, lat] = user.location.coordinates;
    
    const marker: CustomMarker = {
      id: user._id,
      position: {
        lat,
        lng
      },
      tooltip: (
        <div className="marker">
          <img
            src={user.profilePicture || '/default-avatar.png'}
            alt={user.displayName}
            className="marker-image"
          />
        </div>
      ),
      onClick: () => setSelectedUserId(selectedUserId === user._id ? null : user._id)
    };
    return marker;
  }).filter((marker): marker is CustomMarker => marker !== null);

  return (
    <Box sx={{ 
      height: '100%',
      width: '100%',
      position: 'relative',
      overflow: 'hidden', // Prevent double scrollbars
    }}>
      {error && (
        <Typography color="error" sx={{ m: 2 }}>
          {error}
        </Typography>
      )}
      
      <MapComponent
        mapCenter={defaultCenter}
        mapBounds={mapBounds}
        polygonPath={[]}
        cornerMarkers={{}}
        locationName="God View"
        neighborhoodBoundaries={[]}
        height={window.innerHeight - 112}
        width={window.innerWidth}
        customMarkers={userMarkers}
      />
    </Box>
  );
};

export default GodView; 